/**
 * Persist MIDI bridge device/port prefs (machine-local).
 * Control bindings live on the project draft / Export — not here.
 */

import { normalizeControlBinding, type ControlBindingSpec } from "./controlBindings";
import type { AdamControlBinding } from "./controlRouter";
import {
  DEFAULT_MIDI_BRIDGE_CONFIG,
  normalizeMidiBridgeConfig,
  type MidiBridgeConfig,
} from "./midiBridge";
import { normalizeControlPath } from "./controlRegistry";

const STORAGE_KEY = "curva:audio-lab:controls:v1";

export type ControlPrefs = {
  midi: MidiBridgeConfig;
};

export function defaultControlPrefs(): ControlPrefs {
  return {
    midi: { ...DEFAULT_MIDI_BRIDGE_CONFIG },
  };
}

export function loadControlPrefs(): ControlPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultControlPrefs();
    }
    const parsed = JSON.parse(raw) as { midi?: unknown; bindings?: unknown };
    const midi = normalizeMidiBridgeConfig(parsed.midi);
    // Drop legacy bindings field from older controls prefs blobs.
    if ("bindings" in parsed) {
      saveControlPrefs({ midi });
    }
    return { midi };
  } catch {
    return defaultControlPrefs();
  }
}

export function saveControlPrefs(prefs: ControlPrefs): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        midi: normalizeMidiBridgeConfig(prefs.midi),
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function normalizeAdamBindings(value: unknown): AdamControlBinding[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: AdamControlBinding[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const target = normalizeControlPath(
      typeof record.target === "string" ? record.target : "",
    );
    const binding = normalizeControlBinding(record.binding) as ControlBindingSpec | null;
    if (!target || !binding) {
      continue;
    }
    const key = `${target}::${binding.path}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push({ target, binding });
  }
  return out;
}
